import React, { useState } from "react";
import { IconButton, Menu, MenuItem } from "@mui/material";
import { GlobeIcon } from "@phosphor-icons/react";
import { useTranslation } from "react-i18next";
import { track } from "../../lib/analytics";
import { doPost } from "../../utils/Apis";
import { answerLanguageSuggestion } from "../../i18n/languageSuggestion";

const LANGS = [
  { code: "it", label: "Italiano" },
  { code: "en", label: "English" },
];

/**
 * Records a switch of language: for analytics, as the visitor's answer to
 * the site's suggestion of a language, and on the account of whoever is
 * signed in, so the app and its emails follow. The account sync is
 * fire-and-forget and only runs when a session token is present (the funnel's
 * verify phase is pre-login).
 */
export function rememberLanguage(from, to) {
  track("Language Switched", { props: { from, to } });
  answerLanguageSuggestion();
  let hasToken = false;
  try {
    hasToken = !!JSON.parse(localStorage.getItem("userToken") || "null")?.accessToken;
  } catch {
    hasToken = false;
  }
  if (hasToken) {
    const fd = new FormData();
    fd.append("language", to);
    doPost("/profile/language", fd).catch((error) => console.error("The account did not save its new language:", error));
  }
}

/**
 * Standalone EN/IT switcher, extracted from Header so the chrome-less
 * onboarding pages (/signup, /tour) carry their own.
 */
const LanguageToggle = ({ size = "1.25rem" }) => {
  const { i18n } = useTranslation();
  const currentLang = (i18n.language || "it").slice(0, 2);
  const [anchor, setAnchor] = useState(null);

  const setLanguage = (target) => {
    if (target === currentLang) return;
    rememberLanguage(currentLang, target);
    i18n.changeLanguage(target);
  };

  return (
    <>
      <IconButton
        onClick={(e) => setAnchor(e.currentTarget)}
        aria-label="Language"
        aria-haspopup="menu"
        aria-expanded={Boolean(anchor)}
        sx={{
          color: "rgba(255,255,255,0.78)",
          padding: "6px",
          "&:hover": { color: "#fff", background: "transparent" },
        }}
      >
        <GlobeIcon size={size} />
      </IconButton>
      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        sx={{
          mt: 1,
          "& .MuiPaper-root": {
            background:
              "linear-gradient(145deg, rgba(26, 26, 26, 0.95), rgba(42, 42, 42, 0.95))",
            backdropFilter: "blur(20px)",
            border: "1px solid rgba(184, 134, 11, 0.18)",
            borderRadius: "10px",
            minWidth: "150px",
            boxShadow: "0 12px 32px rgba(0, 0, 0, 0.35)",
          },
        }}
      >
        {LANGS.map(({ code, label }) => (
          <MenuItem
            key={code}
            onClick={() => {
              setLanguage(code);
              setAnchor(null);
            }}
            selected={currentLang === code}
            sx={{
              color: currentLang === code ? "#B8860B" : "rgba(255,255,255,0.85)",
              fontWeight: currentLang === code ? 600 : 400,
              fontSize: "0.95rem",
              py: 1,
              "&:hover": { background: "rgba(184, 134, 11, 0.1)", color: "#B8860B" },
              "&.Mui-selected": { background: "rgba(184, 134, 11, 0.08)" },
              "&.Mui-selected:hover": { background: "rgba(184, 134, 11, 0.14)" },
            }}
          >
            {label}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

export default LanguageToggle;
